'use client';

import { Tooltip as TooltipPrimitive } from 'radix-ui';
import type { ReactElement, ReactNode } from 'react';

/**
 * The name of an icon-only control, shown on hover and on keyboard focus
 * (docs/10 §4, Overlays). The control keeps its own accessible name; the tip
 * repeats it for the eyes. Each tip carries its own provider, so an app needs
 * no setup at its root.
 */
export function Tooltip({
    label,
    children,
    side = 'top',
    align = 'center',
}: {
    label: ReactNode;
    /** One element that accepts a ref: the control the tip names. */
    children: ReactElement;
    side?: 'top' | 'right' | 'bottom' | 'left';
    align?: 'start' | 'center' | 'end';
}) {
    return (
        <TooltipPrimitive.Provider delayDuration={300}>
            <TooltipPrimitive.Root>
                <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
                <TooltipPrimitive.Portal>
                    <TooltipPrimitive.Content
                        side={side}
                        align={align}
                        sideOffset={4}
                        className="z-50 rounded-card border border-border bg-popover px-2 py-1 text-12 font-medium text-popover-foreground shadow-popover data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0"
                    >
                        {label}
                    </TooltipPrimitive.Content>
                </TooltipPrimitive.Portal>
            </TooltipPrimitive.Root>
        </TooltipPrimitive.Provider>
    );
}
